import React, { PropsWithChildren, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { IconRegularChevronLeft, IconRegularChevronRight } from '@notino/react-styleguide'

// hooks
import useMessages from '../../hooks/useMessages'
import useScrolledElement from '../../hooks/useScrolledElement'

// utils
import { isElementOverflownX } from '../../utils/helper'

// styles
import * as SC from './ElementsSliderStyles'

type Props = PropsWithChildren<{
	initScrolledElementID?: string
	initScrollBehavior?: ScrollBehavior
	fading?: boolean
	showScrollButtons?: boolean
	hideScrollbar?: boolean
	gap?: string
	overflowX?: 'auto' | 'scroll' | 'hidden'
	asList?: boolean
}>

type ForwardRefs = {
	scrollElementRef: HTMLDivElement | null
}

const ElementsSlider = React.forwardRef<ForwardRefs, Props>((props, ref) => {
	const {
		children,
		initScrolledElementID,
		fading = true,
		showScrollButtons = true,
		hideScrollbar = true,
		initScrollBehavior = 'instant',
		gap = '0px',
		overflowX = 'auto',
		asList = false
	} = props

	const { messages } = useMessages()
	const [isOverflown, setIsOverflown] = useState(false)
	const scrollElementRef = useRef<HTMLDivElement>(null)
	const initElementScrolledRef = useRef(false)

	const { isScrolledLeft, isScrolledRight, scrollLeft, scrollRight } = useScrolledElement(scrollElementRef.current)
	const hasEdgeOverlays = fading || showScrollButtons

	useImperativeHandle(ref, () => ({
		scrollElementRef: scrollElementRef.current
	}))

	useEffect(() => {
		const container = scrollElementRef.current
		if (!container) {
			return undefined
		}

		const checkOverflow = () => {
			setIsOverflown(isElementOverflownX(container))
		}

		checkOverflow()

		const resizeObserver = new ResizeObserver(checkOverflow)
		resizeObserver.observe(container)

		// Content width growth (e.g. lazy-loaded items) may not resize the scroll container itself
		if (container.firstElementChild) {
			resizeObserver.observe(container.firstElementChild)
		}

		return () => {
			resizeObserver.disconnect()
		}
	}, [])

	// Scroll focused children clear of the fade/arrow overlays (native focus scroll ignores partial occlusion)
	useEffect(() => {
		const container = scrollElementRef.current
		if (!container || !hasEdgeOverlays) {
			return undefined
		}

		const handleFocusIn = (event: FocusEvent) => {
			const { target } = event
			if (!(target instanceof HTMLElement) || !container.contains(target) || target === container) {
				return
			}

			const containerRect = container.getBoundingClientRect()
			const targetRect = target.getBoundingClientRect()
			const visibleLeft = SC.FADE_WIDTH_PX
			const visibleRight = containerRect.width - SC.FADE_WIDTH_PX
			const targetLeft = targetRect.left - containerRect.left
			const targetRight = targetRect.right - containerRect.left

			let scrollDelta = 0
			if (targetLeft < visibleLeft) {
				scrollDelta = targetLeft - visibleLeft
			} else if (targetRight > visibleRight) {
				scrollDelta = targetRight - visibleRight
			}

			if (scrollDelta !== 0) {
				container.scrollBy({ left: scrollDelta, behavior: 'smooth' })
			}
		}

		container.addEventListener('focusin', handleFocusIn)

		return () => {
			container.removeEventListener('focusin', handleFocusIn)
		}
	}, [isOverflown, hasEdgeOverlays])

	useEffect(() => {
		const container = scrollElementRef.current

		if (container && initScrolledElementID && !initElementScrolledRef.current) {
			const targetElement = document.getElementById(initScrolledElementID)

			if (targetElement) {
				const containerRect = container.getBoundingClientRect()
				const targetElementRect = targetElement.getBoundingClientRect()

				// Calculate the scroll position
				const targetElementLeft = targetElementRect.left - containerRect.left
				const targetElementRight = targetElementRect.right - containerRect.left
				const containerWidth = containerRect.width

				// Determine if the targetElement is out of view
				if (targetElementLeft < 0 || targetElementRight > containerWidth) {
					// Calculate the new scroll position to center the targetElement
					const newScrollLeft = targetElement.offsetLeft - container.clientWidth / 2 + targetElement.clientWidth / 2

					// Ensure the scroll position is within bounds
					const maxScrollLeft = container.scrollWidth - container.clientWidth
					const boundedScrollLeft = Math.min(Math.max(newScrollLeft, 0), maxScrollLeft)

					// Scroll the container
					container.scrollTo({
						left: boundedScrollLeft,
						behavior: initScrollBehavior
					})

					initElementScrolledRef.current = true
				}
			}
		}
	}, [initScrolledElementID, initScrollBehavior])

	return (
		<SC.ElementsSliderWrapper>
			{fading && isOverflown && isScrolledLeft && <SC.FadeLeft />}
			{fading && isOverflown && isScrolledRight && <SC.FadeRight />}
			<SC.ElementsSlider ref={scrollElementRef} $overflowX={overflowX} $hideScrollbar={hideScrollbar} $hasEdgeOverlays={hasEdgeOverlays}>
				<SC.ElementsSliderContent as={asList ? 'ul' : 'div'} $gap={gap} $asList={asList}>
					{children}
				</SC.ElementsSliderContent>
			</SC.ElementsSlider>
			{showScrollButtons && isOverflown && isScrolledLeft && (
				<SC.ScrollLeftButton type='button' aria-label={messages['Scroll left']} onClick={scrollLeft}>
					<IconRegularChevronLeft color={'icon.primary'} />
				</SC.ScrollLeftButton>
			)}
			{showScrollButtons && isOverflown && isScrolledRight && (
				<SC.ScrollRightButton type='button' aria-label={messages['Scroll right']} onClick={scrollRight}>
					<IconRegularChevronRight color={'icon.primary'} />
				</SC.ScrollRightButton>
			)}
		</SC.ElementsSliderWrapper>
	)
})

export default ElementsSlider
